import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plug, Unplug } from "lucide-react";
import type { Agent, AgentConnectorSummary } from "@/types";
import { spec2 } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListToolbar } from "@/components/ListToolbar";
import { useAgentConnectors } from "@/api/queries/connectors";
import { useSetAgentConnector, useAgentDisconnect } from "@/api/mutations/connectors";
import { ConnectAgentConnectorDialog } from "./ConnectAgentConnectorDialog";

// #231 (UC-3/4/6): Connectors pro Agent — enable/disable + Per-Agent-Connect (scope agent|owner).
// Verfügbarkeit (enable) ist orthogonal zur Permission (allow/ask/deny via Settings).
// Credential-Präzedenz: agent > owner(user) > platform.
export function AgentConnectorsSection({ agent }: { agent: Agent }) {
  const { data, isLoading } = useAgentConnectors(agent.id);
  const setEnabled = useSetAgentConnector(agent.id);
  const disconnect = useAgentDisconnect(agent.id);
  const [connectTarget, setConnectTarget] = useState<AgentConnectorSummary | null>(null);
  const [search, setSearch] = useState("");

  const filtered = (data ?? []).filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()));

  function scopeChip(c: AgentConnectorSummary) {
    // #297 (Review MED-3): NICHT mehr ueber authType urteilen — der ist transport-
    // abgeleitet und log dabei: ein cli-Connector mit Pflicht-extraEnv meldet
    // authType "none", braucht aber sehr wohl einen Eintrag. Die Wahrheit steht in
    // connectSpec (fehlt sie, faellt spec2 auf "unavailable" = nicht bedienbar).
    const m = spec2(c.connectSpec).method;
    if (m === "none" || c.connectedScope === "none") return <span className="text-[11.5px] text-text3">kein Auth</span>;
    if (m === "unavailable") return <span className="text-[11.5px] text-text3">nicht verbindbar</span>;
    if (!c.connected) return <span className="text-[11.5px] text-text3">○ nicht verbunden</span>;
    const label = c.connectedScope === "user" ? "owner" : c.connectedScope; // user-scope = Owner-Konto
    return <span className="text-[11.5px] text-ok-text">● {label}</span>;
  }

  return (
    <SectionCard
      icon={<Plug className="h-[18px] w-[18px]" />}
      title="Connectors"
      description="Remote-MCP-Connectors freischalten + verbinden (Scope agent > owner > platform)"
      action={
        <Link to="/connectors" className="text-[12.5px] font-semibold text-accent-text hover:underline">
          Katalog →
        </Link>
      }
    >
      {isLoading ? (
        <p className="text-[13px] text-text3">Lade …</p>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="text-[13px] text-text3">
          Keine Connectors im Katalog. Lege im <Link to="/connectors" className="text-accent-text hover:underline">Connectors-Katalog</Link> welche an.
        </p>
      ) : (
        <>
          <ListToolbar search={search} onSearch={setSearch} placeholder="Connector suchen…" />
          {filtered.length === 0 ? (
            <p className="text-[13px] text-text3">Kein Connector passt zur Suche.</p>
          ) : (
        <ul className="space-y-1.5" data-testid="agent-connectors-list">
          {filtered.map((c) => {
            // Per-Agent-Trennen nur für agent-/owner-Scope (platform wird im Katalog verwaltet).
            const disconnectScope = c.connectedScope === "agent" ? "agent" : c.connectedScope === "user" ? "owner" : null;
            return (
              <li key={c.connectorId} className="flex items-center gap-3 rounded-md border border-border px-2.5 py-2">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={c.enabled}
                    disabled={setEnabled.isPending}
                    onChange={(e) =>
                      setEnabled.mutate(
                        { connectorId: c.connectorId, enabled: e.target.checked },
                        {
                          onSuccess: () => toast.success(`${c.name} ${e.target.checked ? "aktiviert" : "deaktiviert"}`),
                          onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
                        },
                      )
                    }
                    data-testid={`agent-connector-toggle-${c.connectorId}`}
                  />
                  <span className="text-[13px] font-medium text-text">{c.name}</span>
                </label>
                {/* #297: Verfahren statt des mehrdeutigen authType. */}
                <Badge variant={spec2(c.connectSpec).method === "none" ? "default" : "enrolled"}>{spec2(c.connectSpec).method}</Badge>
                <span className="flex-1">{scopeChip(c)}</span>
                {c.connectedScope !== "none" && spec2(c.connectSpec).method !== "unavailable" && (
                  <Button variant="outline" size="sm" onClick={() => setConnectTarget(c)} data-testid={`agent-connector-connect-${c.connectorId}`}>
                    Verbinden
                  </Button>
                )}
                {disconnectScope && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disconnect.isPending}
                    title={`Trennen (${disconnectScope})`}
                    onClick={() =>
                      disconnect.mutate(
                        { connectorId: c.connectorId, scope: disconnectScope },
                        {
                          onSuccess: () => toast.success(`${c.name} getrennt (${disconnectScope})`),
                          onError: (err) => toast.error(`Trennen fehlgeschlagen: ${(err as Error).message}`),
                        },
                      )
                    }
                    data-testid={`agent-connector-disconnect-${c.connectorId}`}
                  >
                    <Unplug className="h-4 w-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
          )}
        </>
      )}

      {connectTarget && (
        <ConnectAgentConnectorDialog agentId={agent.id} connector={connectTarget} open onOpenChange={(o) => !o && setConnectTarget(null)} />
      )}
    </SectionCard>
  );
}
